import { useCallback, useState, type FocusEvent } from 'react';

export interface LayersRowRevealHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: (event: FocusEvent<HTMLElement>) => void;
}

export interface LayersRowReveal {
  /** True while the row is hovered or contains focus. */
  revealed: boolean;
  /** Spread onto the row's root element. */
  rowHandlers: LayersRowRevealHandlers;
}

/**
 * Hover-**and**-focus-within reveal for a Layers row's trailing actions (move up/down, drag
 * handle, ⋮). Driven by React state rather than a CSS `:hover`/`:focus-within` selector so that:
 *  - it is directly assertable in RTL tests (fire `mouseEnter`/`focus` and read the result), and
 *  - the actions are reachable by keyboard — `:hover`-only reveal leaves a Tab landing on a
 *    control that is still invisible.
 *
 * React normalizes `onFocus`/`onBlur` to bubble (unlike native DOM `focus`/`blur`), so attaching
 * these two handlers on the row's root element alone gives the same behaviour as CSS
 * `:focus-within` — no capture-phase listener is needed.
 */
export function useLayersRowReveal(): LayersRowReveal {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const onMouseEnter = useCallback(() => setHovered(true), []);
  const onMouseLeave = useCallback(() => setHovered(false), []);
  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    // A focus move to another control inside the same row must not close the reveal.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setFocused(false);
  }, []);

  return {
    revealed: hovered || focused,
    rowHandlers: { onMouseEnter, onMouseLeave, onFocus, onBlur }
  };
}
